import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  Injectable,
  Input,
  OnChanges,
  Output,
  signal,
  Signal,
  SimpleChanges,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbInputDatepicker,
} from '@ng-bootstrap/ng-bootstrap';
import { ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

/**
 * This Service handles how the date is rendered and parsed from keyboard i.e. in the bound input field.
 */
@Injectable()
export class CustomDateParserFormatter extends NgbDateParserFormatter {
  readonly DELIMITER = '/';

  parse(value: string) {
    if (value) {
      const date = value.split(this.DELIMITER);
      if (date.length === 3) {
        return {
          day: Number.parseInt(date[0], 10),
          month: Number.parseInt(date[1], 10),
          year: Number.parseInt(date[2], 10 + 2000),
        };
      }
    }
    return null;
  }

  format(date: NgbDateStruct): string {
    return date ? `${date.year}/${this.padNumber(date.month)}/${this.padNumber(date.day)}` : '';
  }

  padNumber(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}

@Component({
  selector: 'app-date-range-picker',
  imports: [NgbInputDatepicker, ReactiveFormsModule, NgClass],
  providers: [{ provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter }],
  templateUrl: './date-range-picker.component.html',
})
export class DateRangePickerComponent implements OnChanges {
  readonly formatter = inject(NgbDateParserFormatter);
  readonly calendar = inject(NgbCalendar);

  @ViewChild('dpFromDate') dpFromDate?: ElementRef;
  @ViewChild('datepicker') datepicker?: NgbCalendar;

  @Input() setMinMaxDateFor: string | undefined;
  @Input() fromDate!: NgbDateStruct | null;
  @Input() toDate!: NgbDateStruct | null;
  @Input() disabled: boolean = false;

  @Output() fromDateChange: EventEmitter<string | null> = new EventEmitter<string | null>();
  @Output() toDateChange: EventEmitter<string | null> = new EventEmitter<string | null>();
  @Output() datesUpdated: EventEmitter<boolean> = new EventEmitter<boolean>();
  hoveredDate: NgbDateStruct | undefined = undefined;
  displayIsRequired?: boolean = false;

  // default has to be set to NgbDate type, cannot be undefined.
  minDate: WritableSignal<NgbDateStruct | null> = signal(this.calendar.getNext(this.calendar.getToday(), 'y', -10));
  maxDate: WritableSignal<NgbDateStruct | null> = signal(this.calendar.getNext(this.calendar.getToday(), 'y', 10));
  fromDateSignal: WritableSignal<NgbDateStruct | null> = signal(null);
  toDateSignal: WritableSignal<NgbDateStruct | null> = signal(null);

  fromDateConverted: Signal<NgbDate> = computed<NgbDate>(() => this.fromDateSignal() as NgbDate);
  minDateConverted: Signal<NgbDate> = computed<NgbDate>(() => this.minDate() as NgbDate);
  maxDateConverted: Signal<NgbDate> = computed<NgbDate>(() => this.maxDate() as NgbDate);

  ngOnChanges(changes: SimpleChanges): void {
    // set values here or revert to default
    this.fromDateSignal.set(this.fromDate ?? null);
    this.toDateSignal.set(this.toDate ?? null);

    if (changes['disabled']?.currentValue === true) {
      this.resetCalendar();
      this.displayIsRequired = false;
    }
    switch (this.setMinMaxDateFor) {
      case 'TopupRules': {
        this.minDate.set(this.calendar.getToday());
        this.maxDate.set(this.calendar.getNext(this.calendar.getToday(), 'y', 5));
        break;
      }
      case 'exportTransactions': {
        this.maxDate.set(this.calendar.getToday());
      }
    }
  }

  onDateSelection(date: NgbDate) {
    // if no dates selected
    if (!this.fromDateSignal() && !this.toDateSignal()) {
      this.fromDateSignal.set(date);
      this.fromDateChange.emit(this.formatter.format(this.fromDateSignal()));
    }
    // if both from and to dates selected
    else if (this.fromDateSignal() && !this.toDateSignal() && date?.after(this.fromDateSignal())) {
      this.toDateSignal.set(date);
      this.toDateChange.emit(this.formatter.format(this.toDateSignal()));
    }
    // else if from date but no to date selected
    else {
      this.toDateSignal.set(null);
      this.fromDateSignal.set(date);
      this.fromDateChange.emit(this.formatter.format(this.fromDateSignal()));
      this.datesUpdated.emit(true);
    }
    this.datesUpdated.emit(true);
    this.displayIsRequired = false;
  }

  isHovered(date: NgbDate) {
    return (
      this.fromDateSignal() &&
      !this.toDateSignal() &&
      this.hoveredDate &&
      date.after(this.fromDateSignal()) &&
      date.before(this.hoveredDate)
    );
  }

  isInside(date: NgbDate) {
    return this.toDateSignal() && date.after(this.fromDateSignal()) && date.before(this.toDateSignal());
  }

  isRange(date: NgbDate) {
    return (
      date.equals(this.fromDateSignal()) ||
      (this.toDateSignal() && date.equals(this.toDateSignal())) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  validateInput(currentValue: NgbDate, input: string): NgbDate | null {
    const parsed = this.formatter.parse(input);
    return parsed && this.calendar.isValid(NgbDate.from(parsed)) ? NgbDate.from(parsed) : currentValue;
  }

  resetCalendar() {
    this.fromDateSignal.set(null);
    this.toDateSignal.set(null);
    this.fromDateChange.emit(this.formatter.format(this.fromDateSignal()));
    this.toDateChange.emit(this.formatter.format(this.toDateSignal()));

    if (this.setMinMaxDateFor === 'exportTransactions') this.displayIsRequired = false;
    if (this.setMinMaxDateFor === 'spendingRules') {
      this.displayIsRequired = true;
      this.minDate.set(this.calendar.getToday());
      this.maxDate.set(this.calendar.getNext(this.calendar.getToday(), 'y', 5));
    }
    return null;
  }

  closeCalendar(datepicker: { close: () => void }, event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    datepicker.close();
    // if only one date selected, set end date to the same value in order to select single date.
    if (this.fromDateSignal() && !this.toDateSignal()) {
      this.toDateChange.emit(this.formatter.format(this.fromDateSignal()));
      this.toDateSignal.set(this.fromDateSignal());
    }
  }

  isPrevDisabled(): boolean {
    if (!this.minDate()) {
      return false; // If minDate is not set, do not disable the previous button
    }

    const today = this.calendar.getToday();
    return this.minDate()?.year === today.year && this.minDate()?.month === today.month;
  }
}
